<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useVideoStore } from './stores/videoStore';
import { usePlaylistStore } from './stores/playlistStore';
import { useGroupStore } from './stores/groupStore';
import { fetchVideos, fetchPlaylists, fetchPlaylistDetail, deleteVideos } from './composables/useApi';
import Toolbar from './components/Toolbar.vue';
import PlaylistSection from './components/PlaylistSection.vue';
import AlbumTabs from './components/AlbumTabs.vue';
import VideoGrid from './components/VideoGrid.vue';
import VideoList from './components/VideoList.vue';
import Pagination from './components/Pagination.vue';
import PlaylistDetail from './components/PlaylistDetail.vue';
import VideoPlayer from './components/VideoPlayer.vue';
import LoadingOverlay from './components/LoadingOverlay.vue';

const videoStore = useVideoStore();
const playlistStore = usePlaylistStore();
const groupStore = useGroupStore();
const route = useRoute();
const playerRef = ref<InstanceType<typeof VideoPlayer> | null>(null);
const toolbarRef = ref<InstanceType<typeof Toolbar> | null>(null);
const albumTabsRef = ref<InstanceType<typeof AlbumTabs> | null>(null);

// Whether we're in group management mode
const isGroupManaging = computed(() => albumTabsRef.value?.isManaging ?? false);

// ── 加载数据 ──

async function loadData() {
  videoStore.loading = true;
  try {
    const [videos, playlists] = await Promise.all([
      fetchVideos(true),
      fetchPlaylists(),
    ]);
    videoStore.allVideos = videos;
    videoStore.highlightDuplicates();
    videoStore.applyFilterAndSort();
    playlistStore.playlists = playlists;
    groupStore.loadGroups();
  } catch (e) {
    console.error('Failed to load data:', e);
  } finally {
    videoStore.loading = false;
  }
}

// ── 事件处理 ──

function handlePlayVideo(id: string, name: string, segmented: boolean) {
  if (playlistStore.showPlaylistDetail) {
    playerRef.value?.playEpisodeFromList(
      playlistStore.currentPlaylist!.episodes.findIndex((e) => e.file_id === id)
    );
  } else {
    playerRef.value?.playStandaloneVideo(id, name, segmented);
  }
}

async function handleDeleteVideo(id: string) {
  try {
    await deleteVideos([id]);
    videoStore.removeVideo(id);
  } catch (e: unknown) {
    alert('删除失败: ' + (e instanceof Error ? e.message : String(e)));
  }
}

async function handleBatchDelete(ids: string[]) {
  try {
    const count = await deleteVideos(ids);
    for (const id of ids) videoStore.removeVideo(id);
    return count;
  } catch (e: unknown) {
    alert('批量删除失败: ' + (e instanceof Error ? e.message : String(e)));
    return 0;
  }
}

async function handlePlaylistSelect(id: string) {
  try {
    const detail = await fetchPlaylistDetail(id);
    playlistStore.openPlaylist(detail);
    if (detail.episodes.length > 0) {
      const epIdx = detail.episodes.findIndex((e) => e.file_id === (route.query.file_id as string));
      playerRef.value?.playEpisodeFromList(epIdx >= 0 ? epIdx : 0);
    }
  } catch (e: unknown) {
    alert('加载播放列表失败: ' + (e instanceof Error ? e.message : String(e)));
  }
}

async function handlePlayAll(id: string) {
  try {
    const detail = await fetchPlaylistDetail(id);
    playlistStore.openPlaylist(detail);
    if (detail.episodes.length > 0) {
      playerRef.value?.playEpisodeFromList(0);
    }
  } catch (e: unknown) {
    alert('加载播放列表失败: ' + (e instanceof Error ? e.message : String(e)));
  }
}

// ── URL 深度链接 ──

watch(
  () => route.query.playlist as string,
  async (playlistId) => {
    if (playlistId) {
      const epIdx = parseInt(route.query.episode as string) || 0;
      try {
        const detail = await fetchPlaylistDetail(playlistId);
        playlistStore.openPlaylist(detail);
        if (detail.episodes[epIdx]) {
          playerRef.value?.playEpisodeFromList(epIdx);
        } else if (detail.episodes.length) {
          playerRef.value?.playEpisodeFromList(0);
        }
      } catch (e: unknown) {
        alert('加载播放列表失败: ' + (e instanceof Error ? e.message : String(e)));
      }
    }
  },
  { immediate: true }
);

// ── 初始化 ──

onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="app-container">
    <!-- 加载中 -->
    <LoadingOverlay v-if="videoStore.loading && videoStore.allVideos.length === 0" />

    <!-- 主内容 -->
    <template v-else>
      <!-- 工具栏 -->
      <Toolbar ref="toolbarRef" v-if="playlistStore.showToolbar" @batch-delete="handleBatchDelete" />

      <!-- 播放时：左右分栏布局 -->
      <template v-if="playlistStore.isPlaying">
        <div class="playback-layout">
          <div class="playback-player">
            <VideoPlayer ref="playerRef" />
          </div>
          <div class="playback-sidebar" v-show="playlistStore.showListWhilePlaying">
            <!-- 统计栏（分组管理时隐藏） -->
            <div v-if="playlistStore.showStats && !isGroupManaging" class="stats-bar">
              <span class="count">
                共 {{ videoStore.allVideos.length }} 个视频
                <template v-if="videoStore.hasSearch">
                  ，搜索 "{{ videoStore.searchQuery }}" 找到 {{ videoStore.totalFiltered }} 个
                </template>
                <template v-else-if="videoStore.totalFiltered !== videoStore.allVideos.length">
                  ，显示 {{ videoStore.totalFiltered }} 个（已过滤）
                </template>
              </span>
            </div>

            <!-- 播放列表区域 -->
            <PlaylistSection
              v-if="playlistStore.showPlaylists"
              :playlists="playlistStore.playlists"
              @select="handlePlaylistSelect"
              @play-all="handlePlayAll"
            />

            <!-- 分组标签栏 -->
            <AlbumTabs />

            <!-- 播放列表详情 -->
            <PlaylistDetail ref="playlistDetailRef" v-if="playlistStore.showPlaylistDetail" />

            <!-- 视频列表/网格 -->
            <template v-if="playlistStore.showGrid">
              <VideoGrid
                v-if="videoStore.viewMode === 'grid'"
                :videos="videoStore.pagedVideos"
                @play="handlePlayVideo"
                @delete="handleDeleteVideo"
                @select="videoStore.toggleSelect($event)"
              />
              <VideoList
                v-else
                :videos="videoStore.pagedVideos"
                @play="handlePlayVideo"
                @delete="handleDeleteVideo"
                @select="videoStore.toggleSelect($event)"
              />
              <Pagination v-if="playlistStore.showPagination && !isGroupManaging" />
            </template>
          </div>
        </div>
      </template>

      <!-- 非播放时：正常全宽布局 -->
      <template v-else>
        <!-- 统计栏 -->
        <div v-if="playlistStore.showStats" class="stats-bar">
          <span class="count">
            共 {{ videoStore.allVideos.length }} 个视频
            <template v-if="videoStore.hasSearch">
              ，搜索 "{{ videoStore.searchQuery }}" 找到 {{ videoStore.totalFiltered }} 个
            </template>
            <template v-else-if="videoStore.totalFiltered !== videoStore.allVideos.length">
              ，显示 {{ videoStore.totalFiltered }} 个（已过滤）
            </template>
          </span>
        </div>

        <!-- 播放列表区域 -->
        <PlaylistSection
          v-if="playlistStore.showPlaylists"
          :playlists="playlistStore.playlists"
          @select="handlePlaylistSelect"
          @play-all="handlePlayAll"
        />

        <!-- 分组标签栏 -->
        <AlbumTabs />

        <!-- 播放列表详情 -->
        <PlaylistDetail ref="playlistDetailRef" v-if="playlistStore.showPlaylistDetail" />

        <!-- 视频列表/网格 -->
        <template v-if="playlistStore.showGrid">
          <VideoGrid
            v-if="videoStore.viewMode === 'grid'"
            :videos="videoStore.pagedVideos"
            @play="handlePlayVideo"
            @delete="handleDeleteVideo"
          />
          <VideoList
            v-else
            :videos="videoStore.pagedVideos"
            @play="handlePlayVideo"
            @delete="handleDeleteVideo"
          />
          <Pagination v-if="playlistStore.showPagination" />
        </template>
      </template>
    </template>
  </div>
</template>

<style scoped>
.app-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 32px 48px;
  min-height: 100vh;
  animation: fadeIn 0.4s ease-out;
}

.stats-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 4px;
}
.stats-bar .count {
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.02em;
}

/* ── 播放时左右分栏 ── */
.playback-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.playback-player {
  flex: 0 0 55%;
  max-width: 55%;
  position: sticky;
  top: 16px;
}

.playback-sidebar {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

@media (max-width: 768px) {
  .app-container {
    padding: 0 0 32px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }

  .stats-bar {
    padding: 0 16px;
    margin-bottom: 12px;
  }
  .stats-bar .count {
    font-size: 13px;
  }

  .playback-layout {
    flex-direction: column;
  }

  .playback-sidebar {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
}
</style>
